import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/cn";
import type { PodiumEntry } from "@/api/types";
import { Badge } from "./Badge";
import { MedalTooltip } from "./MedalTooltip";
import { NumberTicker } from "@/components/effects/NumberTicker";
import { Sparkles } from "@/components/effects/Sparkles";

export interface PodiumCardProps {
  entry: PodiumEntry;
  rank: 1 | 2 | 3;
  metricLabel: string;
}

/** Visual tokens per rank. Kept colocated so the JSX is a straight lookup. */
const RANK_STYLES = {
  1: {
    medalVar: "var(--medal-gold)",
    glowVar: "var(--medal-gold-glow)",
    // Radial gold aura + warm rim.
    bg: "radial-gradient(120% 80% at 50% 0%, rgba(251,191,36,0.22) 0%, rgba(251,191,36,0.04) 45%, rgba(19,17,31,0.9) 100%)",
    border: "rgba(251,191,36,0.45)",
    label: "Campeón",
    extraHeight: 30,
    textShadow: "0 0 24px rgba(251,191,36,0.35)",
  },
  2: {
    medalVar: "var(--medal-silver)",
    glowVar: "var(--medal-silver-glow)",
    // Cold silver/blue sheen.
    bg: "radial-gradient(120% 80% at 50% 0%, rgba(203,213,225,0.14) 0%, rgba(148,163,184,0.04) 45%, rgba(19,17,31,0.9) 100%)",
    border: "rgba(203,213,225,0.3)",
    label: "Subcampeón",
    extraHeight: 0,
    textShadow: "none",
  },
  3: {
    medalVar: "var(--medal-bronze)",
    glowVar: "var(--medal-bronze-glow)",
    // Warm copper.
    bg: "radial-gradient(120% 80% at 50% 0%, rgba(217,119,6,0.18) 0%, rgba(217,119,6,0.04) 45%, rgba(19,17,31,0.9) 100%)",
    border: "rgba(217,119,6,0.35)",
    label: "Bronce",
    extraHeight: 0,
    textShadow: "none",
  },
} as const;

/** Podium card with rank-specific treatment. 1st place gets a subtle 3D
 * tilt on hover, a gold radial aura, floating sparkles and a taller frame
 * so the tournament hierarchy is legible at a glance. 2nd/3rd get cooler
 * silver / warm bronze glows. Signature is unchanged for tab consumers. */
export function PodiumCard({ entry, rank, metricLabel }: PodiumCardProps) {
  const reduced = useReducedMotion();
  const isGold = rank === 1;
  const style = RANK_STYLES[rank];
  const initial = (entry.username ?? "?").charAt(0).toUpperCase();

  // Hover tilt only on the gold card. 4° feels alive without breaking text
  // legibility — anything over 6° starts distorting the score.
  const hoverAnim = reduced
    ? undefined
    : isGold
      ? { rotateY: 4, rotateX: -2, y: -2 }
      : { y: -3 };

  return (
    <motion.article
      className={cn(
        "group relative flex flex-col items-center gap-3 rounded-[20px] border p-6 text-center",
        "backdrop-blur-sm",
      )}
      style={{
        background: style.bg,
        borderColor: style.border,
        boxShadow: isGold
          ? `0 20px 60px -20px ${style.glowVar}, 0 0 80px -30px ${style.glowVar}`
          : `0 12px 40px -20px ${style.glowVar}`,
        minHeight: 260 + style.extraHeight,
        // Enable perspective for the 3D tilt on the gold card.
        transformStyle: "preserve-3d",
        perspective: 900,
      }}
      whileHover={hoverAnim}
      transition={{ type: "spring", stiffness: 220, damping: 18 }}
    >
      {/* Sparkles sit behind everything else. */}
      <Sparkles active={isGold} />

      {/* Floating medal pill above the card. */}
      <div
        aria-hidden
        className={cn(
          "absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-bg-0",
        )}
        style={{
          background: style.medalVar,
          boxShadow: `0 6px 20px -4px ${style.glowVar}`,
        }}
      >
        {rank}º · {style.label}
      </div>

      <div
        className="relative flex h-16 w-16 items-center justify-center rounded-full font-display text-2xl font-bold"
        style={{
          background: `linear-gradient(135deg, ${style.medalVar}, rgba(255,255,255,0.05))`,
          color: "#0f0f1a",
          boxShadow: `0 0 0 2px ${style.border}, 0 8px 24px -6px ${style.glowVar}`,
        }}
        aria-hidden
      >
        {initial}
      </div>

      <MedalTooltip
        medals={
          "medals" in entry && Array.isArray(entry.medals) ? entry.medals : []
        }
      >
        <div className="font-display text-lg font-semibold text-text-1">
          {entry.username ?? "—"}
        </div>
      </MedalTooltip>

      {entry.team && <div className="text-xs text-text-3">{entry.team}</div>}

      {entry.badges.length > 0 && (
        <div className="flex flex-wrap justify-center gap-1">
          {entry.badges.map((b) => (
            <Badge key={b} code={b} />
          ))}
        </div>
      )}

      <div
        className="tabular mt-auto font-display text-3xl font-bold text-text-1"
        style={{ textShadow: style.textShadow }}
      >
        <NumberTicker value={entry.score} duration={isGold ? 900 : 700} />
      </div>
      <div className="text-[11px] uppercase tracking-wider text-text-4">
        {metricLabel}
      </div>
    </motion.article>
  );
}
